import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Chip, Grid } from '@material-ui/core'
import dayjs from 'dayjs'
import Loading from '../../../molecules/Loading'
import EmptySection from '../../../atoms/EmptySection'
import SydButton from '../../../commonDesign/Button'
import SydSelect from '../../../commonDesign/SydSelect'
import FlexRow from '../../../molecules/FlexRow'
import FlexActions from '../../../molecules/FlexActions'
import OperationalTable from '../../../organisms/OperationalTable'
import { useListAllInvocations } from '../../../../api/scheduler'
import InvocationDetailDialog from './InvocationDetailDialog'
import { extractResourceName } from './utils'

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' }
]

/* eslint-disable react/prop-types */
function ErrorCell ({ value }) {
  if (!value) return '\u2014'
  const truncated = value.length > 60 ? value.slice(0, 60) + '\u2026' : value
  return <span title={value}>{truncated}</span>
}
/* eslint-enable react/prop-types */

const columns = [
  {
    id: 'scheduleName',
    accessor: (row) => row.scheduleName || row.scheduleCode || `#${row.scheduleId}`,
    Header: 'Schedule'
  },
  {
    id: 'firedAt',
    accessor: 'firedAt',
    Header: 'Fired At',
    // eslint-disable-next-line react/prop-types
    Cell: ({ value }) => dayjs(value).format('MMM D, YYYY h:mm:ss A')
  },
  {
    id: 'handoffStatus',
    accessor: 'handoffStatus',
    Header: 'Status',
    // eslint-disable-next-line react/prop-types
    Cell: ({ value }) => (
      <Chip
        label={value}
        size='small'
        color={value === 'success' ? 'primary' : 'secondary'}
      />
    )
  },
  {
    id: 'targetArn',
    accessor: 'targetArn',
    Header: 'Target',
    // eslint-disable-next-line react/prop-types
    Cell: ({ value }) => {
      if (!value) return '\u2014'
      return <span title={value}>{extractResourceName(value)}</span>
    }
  },
  {
    id: 'handoffError',
    accessor: 'handoffError',
    Header: 'Error',
    Cell: ErrorCell
  }
]

function ScheduleFireHistory () {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const detailRef = useRef(null)

  const queryParams = useMemo(() => ({
    page,
    pageSize: 25,
    ...(status ? { status } : {})
  }), [page, status])

  const { data: result, isLoading } = useListAllInvocations(queryParams)

  const rows = result?.data ?? []
  const total = result?.total ?? 0
  const pageSize = result?.pageSize ?? 25
  const totalPages = Math.ceil(total / pageSize) || 1

  const handleRowClick = useCallback((row) => {
    detailRef.current?.open(row.original)
  }, [])

  const handleStatusChange = useCallback((e) => {
    setStatus(e.target.value)
    setPage(1)
  }, [])

  if (isLoading && !result) {
    return <Loading />
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow style={{ width: '100%' }}>
            <h2 style={{ margin: 0 }}>Fire History</h2>
            <FlexActions style={{ flex: 1 }}>
              <SydSelect
                value={status}
                onChange={handleStatusChange}
                options={STATUS_OPTIONS}
                minWidth={0}
                placeholder='All Statuses'
                size='sm'
              />
            </FlexActions>
          </FlexRow>
        </Grid>
        <Grid item xs={12}>
          {!rows.length ? (
            <EmptySection
              title='No Invocations'
              description={status ? `No ${status} invocations found.` : 'No invocations recorded yet.'}
            />
          ) : (
            <>
              <OperationalTable
                variant='v2'
                columns={columns}
                data={rows}
                onRowClick={handleRowClick}
                defaultSort={[{ id: 'firedAt', desc: true }]}
              />
              <FlexRow style={{ marginTop: '0.75rem', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#666' }}>
                  Page {page} of {totalPages} ({total} total)
                </span>
                <SydButton size='sm' variant='outline' disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  Prev
                </SydButton>
                <SydButton size='sm' variant='outline' disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  Next
                </SydButton>
              </FlexRow>
            </>
          )}
        </Grid>
      </Grid>

      <InvocationDetailDialog ref={detailRef} />
    </>
  )
}

export default ScheduleFireHistory
